import { useState, useCallback } from "react"
import { User } from "../ZeefTypes"
import ZeefService from "../ZeefService"

interface UseZeefUserReturn {
  // State
  currentUser: User | null
  isLoadingUser: boolean
  isEditingUsername: boolean
  editingUsername: string
  isUpdatingUsername: boolean

  // Actions
  loadCurrentUser: () => Promise<void>
  handleUsernameClick: () => void
  handleUsernameInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  handleUsernameKeyPress: (event: React.KeyboardEvent<HTMLInputElement>) => void
  saveUsername: () => Promise<void>
  cancelUsernameEdit: () => void
}

export const useZeefUser = (): UseZeefUserReturn => {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [isLoadingUser, setIsLoadingUser] = useState(true)
  const [isEditingUsername, setIsEditingUsername] = useState(false)
  const [editingUsername, setEditingUsername] = useState("")
  const [isUpdatingUsername, setIsUpdatingUsername] = useState(false)

  const loadCurrentUser = useCallback(async () => {
    try {
      setIsLoadingUser(true)
      const user = await ZeefService.getCurrentUser()
      setCurrentUser(user)
      setIsLoadingUser(false)
    } catch (error) {
      console.error("Failed to load current user:", error)
      setIsLoadingUser(false)
      setCurrentUser(null)
    }
  }, [])

  const handleUsernameClick = useCallback(() => {
    setIsEditingUsername(true)
    setEditingUsername(currentUser?.username || "")
  }, [currentUser])

  const handleUsernameInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setEditingUsername(event.target.value)
  }, [])

  const handleUsernameKeyPress = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      saveUsername()
    } else if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      cancelUsernameEdit()
    }
  }, [])

  const saveUsername = useCallback(async () => {
    if (!currentUser) return

    if (!editingUsername.trim() || editingUsername.trim() === currentUser.username) {
      cancelUsernameEdit()
      return
    }

    if (editingUsername.trim().length < 1 || editingUsername.trim().length > 50) {
      alert("Username must be between 1 and 50 characters")
      return
    }

    try {
      setIsUpdatingUsername(true)

      const updatedUser = await ZeefService.updateCurrentUser({
        username: editingUsername.trim(),
        email: currentUser.email,
      })

      setCurrentUser(updatedUser)
      setIsEditingUsername(false)
      setEditingUsername("")
      setIsUpdatingUsername(false)
    } catch (error) {
      console.error("Failed to update username:", error)
      alert("Failed to update username: " + (error as Error).message)
      setIsUpdatingUsername(false)
    }
  }, [currentUser, editingUsername])

  const cancelUsernameEdit = useCallback(() => {
    setIsEditingUsername(false)
    setEditingUsername("")
  }, [])

  return {
    // State
    currentUser,
    isLoadingUser,
    isEditingUsername,
    editingUsername,
    isUpdatingUsername,

    // Actions
    loadCurrentUser,
    handleUsernameClick,
    handleUsernameInputChange,
    handleUsernameKeyPress,
    saveUsername,
    cancelUsernameEdit,
  }
}
